import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessagesSquare, ChevronDown, ChevronUp } from "lucide-react";
import { DeskWatchlist } from "./DeskWatchlist";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem("apex_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const VERDICT = {
  BUY: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40",
  WAIT: "text-amber-300 bg-amber-500/15 border-amber-500/40",
  SKIP: "text-rose-300 bg-rose-500/15 border-rose-500/40",
};
const LABEL = { BUY: "ACHETER", WAIT: "ATTENDRE", SKIP: "PASSER" };

const Session = ({ s, open, onToggle }) => (
  <div className="border border-slate-700/60 rounded-xl bg-slate-900/40" data-testid={`desk-session-${s.id}`}>
    <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800/40 transition-colors rounded-xl" data-testid={`desk-session-toggle-${s.id}`}>
      <span className="font-bold text-slate-100 w-24 shrink-0">{s.ticker}</span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${VERDICT[s.verdict]}`} data-testid={`desk-verdict-${s.id}`}>
        {LABEL[s.verdict]} · {s.confidence}%
      </span>
      <span className="text-xs text-slate-400 truncate flex-1">{s.reason}</span>
      <span className="text-[11px] text-slate-500 shrink-0">{new Date(s.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
      {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
    </button>
    {open && (
      <div className="px-4 pb-4 space-y-2">
        {s.messages.map((m, i) => (
          <div key={i} className={`rounded-lg px-3 py-2 text-xs leading-relaxed ${m.agent === "skeptic" ? "bg-slate-800 border border-slate-600" : "bg-slate-800/50"}`}>
            <div className="font-semibold text-slate-300 mb-0.5">{m.name}</div>
            <div className="text-slate-200 whitespace-pre-wrap">{m.text.replace(/\*\*/g, "")}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DeskPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(null);
  const [tk, setTk] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ask = async () => {
    if (!tk.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/api/sniper/desk/run`, { ticker: tk.trim() }, { headers: authHeaders() });
      setSessions((s) => [r.data, ...s]);
      setOpen(r.data.id);
      setTk("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Le desk n'a pas pu répondre");
    }
    setBusy(false);
  };

  useEffect(() => {
    const load = () => axios.get(`${API}/api/sniper/desk?limit=15`, { headers: authHeaders() })
      .then((r) => setSessions(r.data.sessions)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="bg-slate-900/50 border border-cyan-700/30 rounded-2xl p-5" data-testid="desk-panel">
      <div className="flex items-center gap-2 mb-1">
        <MessagesSquare className="w-5 h-5 text-cyan-400" />
        <h3 className="font-bold text-slate-100">Desk de trading — 6 agents</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Avant chaque achat : Scanner, News, Macro, Technicien et Risque débattent, puis le Sceptique tranche. Le Sniper n'achète que sur « ACHETER ».
      </p>
      <DeskWatchlist />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={tk} onChange={(e) => setTk(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ticker : NVDA, NOVO-B.CO, AL2SI.PA, FRES.L…"
          className="bg-slate-900 border border-slate-700 focus:border-cyan-500 outline-none rounded-lg px-3 py-2 text-sm w-72"
          data-testid="desk-ticker-input" />
        <button onClick={ask} disabled={busy || !tk.trim()}
          className="text-sm font-semibold rounded-full px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white transition-colors"
          data-testid="desk-run-btn">
          {busy ? "Le desk débat… (~20 s)" : "Réunir le desk"}
        </button>
        <span className="text-[11px] text-slate-500">Europe : .PA .DE .AS .CO .MI · UK : .L — aucun ordre passé</span>
        {err && <span className="text-xs text-rose-300 w-full" data-testid="desk-run-error">{err}</span>}
      </div>
      {sessions.length === 0
        ? <div className="text-xs text-slate-500" data-testid="desk-empty">Aucune discussion pour l'instant — le desk se réunit dès que le scan trouve un candidat.</div>
        : <div className="space-y-2">{sessions.map((s) => (
            <Session key={s.id} s={s} open={open === s.id} onToggle={() => setOpen(open === s.id ? null : s.id)} />
          ))}</div>}
    </div>
  );
};
